import { FC, useState } from "react";
import { FormField } from "../../../../ui-kit/FormField/FormField";
import { useHandleForm } from "../../../../hooks/useHandleForm";
import { Controller, SubmitHandler } from "react-hook-form";
import {
  AutoComplete,
  ICheckbox,
  IconButton,
  IDatePicker,
  ISelect,
  Spinner,
} from "../../../../ui-kit";
import api from "../../../Admin/api";
import nsiOrgStore from "../../../../store/nsiOrganizationsStore";
import { buildingsStore } from "../../../../store/buildings";
import { observer } from "mobx-react-lite";
import { FormBlock } from "../../../../ui-kit";
import { pick } from "lodash";
import schema from "./validateSchema";
import styles from "./BuildingForm.module.scss";
import classNames from "classnames";
import { useStatusMessage } from "../../../../hooks/useStatusMessage";

export interface IBuildingForm {
  id: number;
  name: string;
  address: string;
  fiasAddressId: string;
  organizationMainId: number;
  organizationMainName: string;
  ownershipTypeId: number;
  funcTypeGroupId: number;
  isRoom: boolean;
  startExplYear: number;
  totalArea: number;
  effectiveArea: number;
  heatedArea: number;
}

const BuildingForm: FC<{
  record;
  addBuildingFromEnergyProfile?: boolean;
  closeForm?: (id?) => void;
}> = observer(
  ({ record: building, addBuildingFromEnergyProfile = false, closeForm }) => {
    const [sendingServer, setSendingServer] = useState(false);

    const {
      control,
      errors,
      handleSubmit,
      register,
      reset,
      setValue,
      watchAllField,
    } = useHandleForm(schema, building);

    const { messageStatus, updateBuilding } = nsiOrgStore;

    useStatusMessage(messageStatus);

    const handleChangeOrganization = (_, organization) => {
      errors.organizationMainId = null;
      setValue("organizationMainId", organization?.id);
    };

    const handleChangeAddress = (_, addressObj) => {
      errors.fiasAddressId = null;
      setValue("fiasAddressId", addressObj?.id);
    };

    const onSubmit: SubmitHandler<IBuildingForm> = async (data) => {
      setSendingServer(true);
      updateBuilding(
        pick(data, [
          "id",
          "name",
          "fiasAddressId",
          "organizationMainId",
          "ownershipTypeId",
          "funcTypeGroupId",
          "isRoom",
          "startExplYear",
          "totalArea",
          "effectiveArea",
          "heatedArea",
        ]),
        () => {
          if (data.id) {
            reset();
          }
          setSendingServer(false);
          closeForm();
        },
        addBuildingFromEnergyProfile
          ? () => buildingsStore.getBuildingsTab(building.orgId)
          : null,
      )
        .then((_) => {
          setSendingServer(false);
        })
        .finally(() => {
          setSendingServer(false);
        });
    };

    const fiasTooltip = (
      <>
        <div>
          Начните вводить адрес с улицы и выберите значение из списка. Например:
        </div>
        <div>
          - фонтанка (для адреса г Санкт-Петербург, наб Реки Фонтанки, д 172)
        </div>
        <div>
          - линия (для ареса г Санкт-Петербург, г Сестрорецк, 9-я линия, д 6 стр
          1)
        </div>
        <div>- советская 9 (г Санкт-Петербург, ул 9-я Советская, д 3)</div>
        <div>
          - большой пр-кт (для адреса г Санкт-Петербург, пр-кт Большой В.О., д
          92 литера Г)
        </div>
        <div>
          - угловые дома - ломоносова 37/8: (для адреса г Санкт-Петербург, город
          Красное Село, ул Ломоносова, д 37/8 литера А)
        </div>
      </>
    );

    return (
      <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
        <FormBlock className={styles.formBlock}>
          <div className={styles.fieldsGrid}>
            <FormField
              type="text"
              label="Наименование здания, строения, сооружения"
              name={"name"}
              currentValue={watchAllField.name}
              register={register}
              error={errors.name?.message}
              isRequired
            />

            <Controller
              control={control}
              name="addressText"
              render={({ field }) => (
                <div className={classNames(styles.fiasField)}>
                  <AutoComplete
                    field={field}
                    label="Адрес"
                    error={
                      errors.fiasAddressId?.message ||
                      errors.addressText?.message
                    }
                    onChange={handleChangeAddress}
                    required={true}
                    showTip={true}
                    tipText="Начните вводить адрес улицы и выберите значение из списка. Например: фонтанка (для адреса г Санкт-Петербург, наб Реки Фонтанки, д 172)"
                    info={fiasTooltip}
                    placement="bottom"
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name="organizationMainName"
              render={({ field }) => (
                <AutoComplete
                  field={field}
                  label="Организация"
                  required
                  getData={api.getAllOrganizations}
                  onChange={handleChangeOrganization}
                  error={
                    errors.organizationMainId?.message ||
                    errors.organizationMainName?.message
                  }
                  disabled={!addBuildingFromEnergyProfile}
                />
              )}
            />

            <Controller
              control={control}
              name="ownershipTypeId"
              render={({ field }) => (
                <ISelect
                  field={field}
                  optionsArr={nsiOrgStore.ownershipTypeList}
                  placeholder={"Вид собственности"}
                  error={errors.ownershipTypeId?.message}
                  isRequired
                />
              )}
            />

            <Controller
              control={control}
              name="funcTypeGroupId"
              render={({ field }) => (
                <ISelect
                  field={field}
                  optionsArr={nsiOrgStore.ftg}
                  placeholder={"Функционально-типологическая группа"}
                  error={errors.funcTypeGroupId?.message}
                  isRequired
                />
              )}
            />

            <Controller
              control={control}
              name="startExplYear"
              render={({ field }) => (
                <IDatePicker
                  field={field}
                  placeholder={"Дата ввода в эксплуатацию"}
                  format="DD.MM.YYYY"
                  picker="date"
                  error={errors.startExplYear?.message}
                  isRequired
                />
              )}
            />

            <FormField
              type="decimal"
              label={
                <>
                  Общая площадь, м<sup>2</sup>
                </>
              }
              name="totalArea"
              currentValue={watchAllField.totalArea}
              register={register}
              error={errors.totalArea?.message}
              isRequired
            />

            <FormField
              type="decimal"
              label={
                <>
                  Полезная площадь, м<sup>2</sup>
                </>
              }
              name="effectiveArea"
              currentValue={watchAllField.effectiveArea}
              register={register}
              error={errors.effectiveArea?.message}
              isRequired
            />
            <FormField
              type="decimal"
              label={
                <>
                  Отапливаемая площадь, м<sup>2</sup>
                </>
              }
              name="heatedArea"
              currentValue={watchAllField.heatedArea}
              register={register}
              error={errors.heatedArea?.message}
              isRequired
            />

            <Controller
              control={control}
              name="isRoom"
              render={({ field: { value, onChange } }) => (
                <ICheckbox
                  checked={value}
                  onChange={onChange}
                  label={"Часть здания"}
                  className={styles.checkbox}
                />
              )}
            />
          </div>
          <div className={styles.buttons}>
            <IconButton
              typeIcon="new"
              className={styles.submitButton}
              isDisabled={sendingServer}
            >
              {sendingServer ? <Spinner /> : "Сохранить"}
            </IconButton>
            <IconButton
              typeIcon="new"
              variant="outlined"
              onClick={(e) => {
                e.preventDefault();
                reset();
                closeForm(building.id);
              }}
            >
              Отмена
            </IconButton>
          </div>
        </FormBlock>
      </form>
    );
  },
);

export default BuildingForm;
